import React, { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { playFlip } from "../lib/sound";

export const CharacterCard = ({ name, role, index = 0, teamLabel }) => {
  const [revealed, setRevealed] = useState(false);
  const isFor = role === "FOR";
  const stampClass = isFor ? "stamp-for" : "stamp-against";
  const accentBg = isFor ? "#39FF14" : "#FF2A54";
  const playerLabel = teamLabel || `Player ${index + 1}`;

  const toggle = () => {
    playFlip();
    setRevealed((v) => !v);
  };

  return (
    <motion.div
      initial={{ y: 60, opacity: 0, rotateZ: index === 0 ? -6 : 6 }}
      animate={{ y: 0, opacity: 1, rotateZ: index === 0 ? -3 : 3 }}
      transition={{
        type: "spring",
        stiffness: 90,
        damping: 14,
        delay: index * 0.15,
      }}
      whileHover={{ rotateZ: 0, y: -4, transition: { duration: 0.2 } }}
      className="relative w-full max-w-[320px] aspect-[3/4]"
      style={{ perspective: "1200px" }}
      data-testid={`character-card-${index}`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={revealed ? "Hide card" : "Reveal card"}
        data-testid={`flip-card-${index}`}
        className="relative w-full h-full focus:outline-none cursor-pointer"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* The flipping inner */}
        <motion.div
          className="absolute inset-0"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ rotateY: revealed ? 180 : 0 }}
          transition={{ duration: 0.55, type: "spring", stiffness: 120, damping: 16 }}
        >
          {/* BACK (face-down, shown first) */}
          <div
            className="absolute inset-0 border-4 border-[#111111] shadow-brutal-lg flex flex-col items-center justify-center bg-retro-stripes"
            style={{ backfaceVisibility: "hidden" }}
            data-testid={`card-back-${index}`}
          >
            <div className="bg-[#FFF4E0] border-4 border-[#111111] px-4 py-3 -rotate-3">
              <div className="font-mono-debate font-bold uppercase tracking-[0.25em] text-xs text-[#111111] text-center">
                {playerLabel}
              </div>
              <div className="font-anton uppercase text-3xl md:text-4xl text-[#111111] text-center leading-none mt-1">
                TAP TO REVEAL
              </div>
            </div>
            <div className="absolute bottom-3 right-3 bg-[#FFD700] border-2 border-[#111111] px-2 py-1 flex items-center gap-1">
              <Eye size={14} />
              <span className="font-mono-debate font-bold uppercase text-[10px] tracking-widest">
                Secret
              </span>
            </div>
            <div className="absolute top-3 left-3 bg-[#111111] text-[#FFD700] px-2 py-1 font-anton uppercase text-base">
              ★ MD ★
            </div>
          </div>

          {/* FRONT (face-up, after flip) */}
          <div
            className="absolute inset-0 bg-white border-4 border-[#111111] shadow-brutal-lg p-6 flex flex-col justify-between"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
            data-testid={`card-front-${index}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono-debate font-bold uppercase text-[10px] tracking-[0.2em] bg-[#111111] text-[#FFF4E0] px-2 py-1">
                {playerLabel}
              </span>
              <span
                className="font-mono-debate font-bold uppercase text-[10px] tracking-[0.2em] px-2 py-1 border-2 border-[#111111]"
                style={{ backgroundColor: accentBg, color: "#111111" }}
              >
                YOUR PICK
              </span>
            </div>

            <div className="flex-1 flex items-center justify-center px-2 py-3">
              <h3
                className="font-anton uppercase text-2xl md:text-3xl leading-tight text-center break-words"
                data-testid={`character-name-${index}`}
              >
                {name}
              </h3>
            </div>

            <div className="flex items-end justify-between">
              <div className="font-mono-debate text-[10px] uppercase tracking-widest text-[#111111]/50 flex items-center gap-1">
                <EyeOff size={12} /> Tap to hide
              </div>
              <motion.div
                initial={{ scale: 0, rotate: 0 }}
                animate={{ scale: 1, rotate: isFor ? -15 : 12 }}
                transition={{ delay: 0.25, type: "spring", stiffness: 200 }}
                className={`font-anton uppercase text-2xl md:text-3xl px-3 py-1 ${stampClass}`}
                data-testid={`role-stamp-${index}`}
              >
                {role}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </button>
    </motion.div>
  );
};
